import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { BigSaleBanner } from '../../../shared/big-sale-banner/big-sale-banner';
import { ProductCard } from '../../../shared/product-card/product-card';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LatestInsights } from '../../../shared/latest-insights/latest-insights';

import { SAVING_ARENA_API_CONFIG } from '../../../core/api/api.config';
import { FrontendBlog } from '../../../core/api/api.models';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { TrustedCmsHtmlPipe } from '../../../core/api/trusted-cms-html.pipe';
import { SeoService } from '../../../core/seo/seo.service';
import { SsrResponseService } from '../../../core/seo/ssr-response.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { faqPageSchema, webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { StoreLogo } from '../../../shared/store-logo/store-logo';
import { AffiliateLinkService } from '../../../core/affiliate/affiliate-link.service';
import { formatOfferCount } from '../../../core/format/offer-count';

type OfferType = 'coupon' | 'deal' | 'new';
type PopularCategoryThumb = 'men' | 'women' | 'accessories' | 'outerwear' | 'shoes';

interface OfferTab {
  id: 'all' | OfferType;
  label: string;
}

interface CategoryStore {
  name: string;
  logoUrl: string;
  metrics?: string;
  slug?: string;
  couponCodeCount?: number;
  couponDealCount?: number;
  couponsCount?: number;
}

interface CategoryOffer {
  store: string;
  title: string;
  savings: string;
  expires: string;
  type: OfferType;
}

interface DetailFaq {
  question: string;
  answer: string;
}

interface ContentTab {
  id: string;
  label: string;
}

interface JournalPost {
  id: number;
  category: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  imageUrl: string;
  storeLogoUrl: string;
}

interface PopularCategoryItem {
  label: string;
  thumb?: PopularCategoryThumb;
  slug?: string;
  imageUrl?: string;
  iconHtml?: SafeHtml | null;
}

interface PopularCategoryTab {
  id: string;
  label: string;
  categories: PopularCategoryItem[];
}

interface TrendingCouponItem {
  id: string;
  type: OfferType;
  storeName: string;
  storeSlug: string;
  storeDomain: string;
  logoUrl: string;
  title: string;
  expires: string;
  actionType: 'code' | 'deal';
  actionLabel: string;
  actionUrl: string;
  isLatest: boolean;
}

interface CompetitorStoreSeed {
  name: string;
  domain: string;
}

interface CompetitorStore extends CompetitorStoreSeed {
  logoUrl: string;
  logoAlt: string;
}

interface AllCategoryStore {
  name: string;
  logoUrl: string;
  slug?: string;
  couponsCount?: number;
}

interface CategoryProductCard {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  hoverImageUrl: string;
  isExternal: boolean;
  /** In-app product page for the card's "View Detail" action. */
  detailPath: string;
  oldPrice: string;
  newPrice: string;
}

/**
 * One hero stat, straight from the API's `statCards` (Active Coupons / Partner Stores /
 * Avg. Savings / Total Products). The backend already formats the number — "2.5K", "36%" — so the
 * page prints `displayValue` and never re-derives it.
 */
interface CategoryStatCard {
  key: string;
  label: string;
  value: number;
  displayValue: string;
}

interface CategoryDetailData {
  name: string;
  eyebrow: string;
  headline: string;
  summary: string;
  heroImageUrl: string;
  stats: { label: string; value: string }[];
  stores: CategoryStore[];
  offers: CategoryOffer[];
  playbook: { title: string; body: string }[];
  faqs: DetailFaq[];
}

@Component({
  selector: 'app-category-detail-page',
  imports: [
    Breadcrumb,
    RouterLink,
    LatestInsights,
    TrustedCmsHtmlPipe,
    ProductCard,
    BigSaleBanner,
    StoreLogo,
  ],
  templateUrl: './category-detail-page.html',
  styleUrl: './category-detail-page.scss',
})
export class CategoryDetailPage {
  /**
   * A computed, because the last crumb is the category's own name and that only arrives with the
   * API response. Until then the trail is "Home / Categories", which is still correct.
   */
  protected readonly crumbs = computed(() => {
    const name = this.categoryName();
    return breadcrumbTrail(
      { label: 'Categories', path: '/categories' },
      ...(name ? [{ label: name }] : []),
    );
  });

  private readonly route = inject(ActivatedRoute);
  private readonly affiliate = inject(AffiliateLinkService);
  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly competitorLogoPool: string[] = [
    '/images/top-stores/logo-amazon.png',
    '/images/top-stores/logo-etsy.png',
    '/images/top-stores/logo-kohls.png',
    '/images/top-stores/logo-rakuten.png',
    '/images/top-stores/logo-zaful.png',
    '/images/top-stores/logo-adulttime.png',
    '/images/top-stores/logo-dresslily.png',
    '/images/top-stores/logo-alibaba.png',
  ];

  readonly offerTabs: OfferTab[] = [
    { id: 'all', label: 'All Offers' },
    { id: 'coupon', label: 'Coupons' },
    { id: 'deal', label: 'Deals' },
    { id: 'new', label: 'New Today' },
  ];

  readonly selectedOfferTabId = signal<OfferTab['id']>('all');
  readonly trendingCouponTabs: OfferTab[] = [
    { id: 'all', label: 'All' },
    { id: 'coupon', label: 'Coupons' },
    { id: 'deal', label: 'Deals' },
    { id: 'new', label: 'New Today' },
  ];
  readonly selectedTrendingCouponTabId = signal<OfferTab['id']>(this.trendingCouponTabs[0].id);
  /**
   * This category's own sub-categories, and nothing else.
   *
   * There used to be two tabs here — "Coupons & Offers" and "Products & Deals" — each seeded with
   * eight hardcoded labels ("Outerwear", "Streetwear", "Bags"…) that matched no category the site
   * runs. The real sub-categories replaced the first tab's list once they loaded; the second tab
   * kept showing the invented ones forever.
   *
   * The section is a way to narrow into a sub-category, so there is nothing to tab between: the
   * whole thing renders only when this category actually has sub-categories (see
   * `hasSubCategories`), and shows exactly those.
   */
  private readonly subCategories = signal<PopularCategoryItem[]>([]);
  readonly hasSubCategories = computed(() => this.subCategories().length > 0);
  readonly popularCategoryTabs = computed<PopularCategoryTab[]>(() => [
    { id: 'sub-categories', label: 'Sub Categories', categories: this.subCategories() },
  ]);

  readonly popularCardsPerSlide = 5;
  readonly selectedPopularCategoryTabId = signal('sub-categories');
  readonly currentPopularCategorySlide = signal(0);
  readonly journalTabs: ContentTab[] = [
    { id: 'all', label: 'All' },
    { id: 'technology', label: 'Technology' },
    { id: 'design', label: 'Design' },
    { id: 'strategy', label: 'Strategy' },
    { id: 'tutorials', label: 'Tutorials' },
  ];
  readonly selectedJournalTabId = signal(this.journalTabs[0].id);

  // Re-read on every paramMap emission, not once from the snapshot. The router reuses this
  // component when only :categorySlug changes, so a snapshot read left the slug pinned to
  // whichever category was opened first — moving from a parent to one of its sub-categories
  // changed the URL and nothing else. See the paramMap subscription in the constructor.
  private categorySlug =
    this.route.snapshot.paramMap.get('categorySlug')?.toLowerCase().trim() ?? 'apparel';

  private readonly categoryBySlug: Record<string, CategoryDetailData> = {
    apparel: {
      name: 'Apparel',
      eyebrow: 'Category Detail',
      headline: 'Premium Apparel Savings Hub',
      summary:
        'Find curated fashion offers from high-converting stores, optimized by size availability, threshold value, and shipping economics.',
      heroImageUrl: '/images/home-banner.jpg',
      stats: [
        { label: 'Active Coupons', value: '128' },
        { label: 'Partner Stores', value: '12' },
        { label: 'Avg. Savings', value: '34%' },
        { label: 'Total Products', value: '3.2K' },
      ],
      stores: [
        {
          name: 'Zaful',
          logoUrl: '/images/top-stores/logo-zaful.png',
          metrics: '38 coupons · 14 deals',
        },
        {
          name: 'Dresslily',
          logoUrl: '/images/top-stores/logo-dresslily.png',
          metrics: '24 coupons · 9 deals',
        },
        {
          name: "Kohl's",
          logoUrl: '/images/top-stores/logo-kohls.png',
          metrics: '31 coupons · 12 deals',
        },
        {
          name: 'Amazon',
          logoUrl: '/images/top-stores/logo-amazon.png',
          metrics: '35 coupons · 11 deals',
        },
      ],
      offers: [
        {
          store: 'Zaful',
          title: 'Extra 25% off seasonal dresses',
          savings: '25% OFF',
          expires: 'Ends in 2 days',
          type: 'coupon',
        },
        {
          store: "Kohl's",
          title: 'Buy 2, get 1 free activewear picks',
          savings: 'B2G1',
          expires: 'Weekend only',
          type: 'deal',
        },
        {
          store: 'Dresslily',
          title: 'New member unlock: $20 off $99',
          savings: '$20 OFF',
          expires: 'New today',
          type: 'new',
        },
        {
          store: 'Amazon',
          title: 'Fashion essentials from $14.99',
          savings: 'FROM $14.99',
          expires: 'Rolling update',
          type: 'deal',
        },
      ],
      playbook: [
        {
          title: 'Start With Fit-Safe Categories',
          body: 'Prioritize tees, knitwear, and adjustable silhouettes first; these produce fewer returns while still unlocking stronger threshold coupons.',
        },
        {
          title: 'Model The Final Cart Total',
          body: 'Compare percent discount against fixed-value code after shipping. A lower-looking discount can still produce a better payable total.',
        },
        {
          title: 'Time Around Drop Windows',
          body: 'Apparel campaigns rotate quickly. Watch refresh periods and buy when size availability and code strength overlap.',
        },
      ],
      faqs: [
        {
          question: 'Can I combine apparel coupons with sale pricing?',
          answer:
            'Usually yes for selected lines. Check exclusions for brand-collab or clearance-only items before checkout.',
        },
        {
          question: 'How do I avoid coupon failure at checkout?',
          answer:
            'Confirm minimum spend, eligible categories, and shipping region first. Then re-test with one backup code.',
        },
      ],
    },
    electronics: {
      name: 'Electronics',
      eyebrow: 'Category Detail',
      headline: 'High-End Electronics Deal Desk',
      summary:
        'Track premium device pricing, verified checkout incentives, and timing windows for the strongest seasonal markdowns.',
      heroImageUrl: '/images/sponsored-brands/thumb-payday-sale.jpg',
      stats: [
        { label: 'Active Coupons', value: '156' },
        { label: 'Partner Stores', value: '15' },
        { label: 'Avg. Savings', value: '22%' },
        { label: 'Total Products', value: '5.4K' },
      ],
      stores: [
        {
          name: 'Amazon',
          logoUrl: '/images/top-stores/logo-amazon.png',
          metrics: '52 coupons · 20 deals',
        },
        {
          name: 'Rakuten',
          logoUrl: '/images/top-stores/logo-rakuten.png',
          metrics: '28 coupons · 13 deals',
        },
        {
          name: 'Alibaba',
          logoUrl: '/images/top-stores/logo-alibaba.png',
          metrics: '43 coupons · 15 deals',
        },
        {
          name: 'Etsy',
          logoUrl: '/images/top-stores/logo-etsy.png',
          metrics: '33 coupons · 10 deals',
        },
      ],
      offers: [
        {
          store: 'Amazon',
          title: 'Noise-cancelling headphones flash drop',
          savings: '$90 OFF',
          expires: 'Ends tonight',
          type: 'deal',
        },
        {
          store: 'Rakuten',
          title: 'Cashback stack on smart home kits',
          savings: '12% BACK',
          expires: '48 hours',
          type: 'coupon',
        },
        {
          store: 'Alibaba',
          title: 'New listing promo on tablets',
          savings: '18% OFF',
          expires: 'New today',
          type: 'new',
        },
      ],
      playbook: [
        {
          title: 'Compare Warranty + Final Price',
          body: 'Always benchmark after-shipping total with warranty terms, not sticker discount only.',
        },
        {
          title: 'Use Launch-to-Clearance Curve',
          body: 'Wait for first markdown cycle unless inventory is constrained in your target model.',
        },
        {
          title: 'Leverage Bundle Economics',
          body: 'Accessory bundles can beat standalone discounts when coupon eligibility is broad.',
        },
      ],
      faqs: [
        {
          question: 'Are refurbished devices included in coupon offers?',
          answer:
            'Some stores include certified-refurbished inventory; check each code condition panel.',
        },
        {
          question: 'Should I split orders to maximize savings?',
          answer: 'Only if shipping and handling do not erase the extra code value.',
        },
      ],
    },
    'ai-tools': {
      name: 'AI Tools',
      eyebrow: 'Category Detail',
      headline: 'Advanced AI Offer Intelligence',
      summary:
        'Compare AI subscriptions, annual billing leverage, and promo windows designed for creators, teams, and automation workflows.',
      heroImageUrl: '/images/sponsored-brands/thumb-new-year.jpg',
      stats: [
        { label: 'Active Coupons', value: '84' },
        { label: 'Partner Stores', value: '8' },
        { label: 'Avg. Savings', value: '29%' },
        { label: 'Total Products', value: '1.8K' },
      ],
      stores: [
        {
          name: 'Rakuten',
          logoUrl: '/images/top-stores/logo-rakuten.png',
          metrics: '17 coupons · 6 deals',
        },
        {
          name: 'Amazon',
          logoUrl: '/images/top-stores/logo-amazon.png',
          metrics: '14 coupons · 5 deals',
        },
        {
          name: 'Alibaba',
          logoUrl: '/images/top-stores/logo-alibaba.png',
          metrics: '21 coupons · 7 deals',
        },
        {
          name: 'Etsy',
          logoUrl: '/images/top-stores/logo-etsy.png',
          metrics: '12 coupons · 4 deals',
        },
      ],
      offers: [
        {
          store: 'Rakuten',
          title: 'AI writing suite annual upgrade',
          savings: '30% OFF',
          expires: 'Ends in 36 hours',
          type: 'coupon',
        },
        {
          store: 'Alibaba',
          title: 'Vision model starter credits',
          savings: '$120 CREDITS',
          expires: 'New today',
          type: 'new',
        },
        {
          store: 'Amazon',
          title: 'Prompt engineering bundles',
          savings: 'SAVE 18%',
          expires: 'This week',
          type: 'deal',
        },
      ],
      playbook: [
        {
          title: 'Benchmark Annual vs Monthly',
          body: 'AI platforms often hide best value in annual plans combined with limited-time acquisition codes.',
        },
        {
          title: 'Optimize Seat Count First',
          body: 'Choose user tier and workload profile before applying a code so you avoid overbuying feature bundles.',
        },
        {
          title: 'Track Credit Expiry Terms',
          body: 'Credit-based offers can look strong but expire quickly; prioritize longer validity if usage is uncertain.',
        },
      ],
      faqs: [
        {
          question: 'Do AI coupons apply to API usage too?',
          answer: 'Some are plan-only, others include usage credits. Review terms before checkout.',
        },
        {
          question: 'When are AI offers strongest?',
          answer:
            'Launch cycles and quarter-end pushes usually deliver the highest effective discounts.',
        },
      ],
    },
    'business-software': {
      name: 'Business Solutions',
      eyebrow: 'Category Detail',
      headline: 'Business Software Savings Command Center',
      summary:
        'Audit deal quality across CRM, analytics, billing, and collaboration stacks while protecting renewals and onboarding costs.',
      heroImageUrl: '/images/home-banner.jpg',
      stats: [
        { label: 'Active Coupons', value: '96' },
        { label: 'Partner Stores', value: '9' },
        { label: 'Avg. Savings', value: '31%' },
        { label: 'Total Products', value: '2.6K' },
      ],
      stores: [
        {
          name: "Kohl's",
          logoUrl: '/images/top-stores/logo-kohls.png',
          metrics: '18 coupons · 8 deals',
        },
        {
          name: 'Rakuten',
          logoUrl: '/images/top-stores/logo-rakuten.png',
          metrics: '22 coupons · 9 deals',
        },
        {
          name: 'Amazon',
          logoUrl: '/images/top-stores/logo-amazon.png',
          metrics: '27 coupons · 10 deals',
        },
        {
          name: 'Alibaba',
          logoUrl: '/images/top-stores/logo-alibaba.png',
          metrics: '19 coupons · 7 deals',
        },
      ],
      offers: [
        {
          store: 'Rakuten',
          title: 'CRM Pro annual bundle',
          savings: '35% OFF',
          expires: 'Ends Sunday',
          type: 'coupon',
        },
        {
          store: 'Amazon',
          title: 'Project suite team plan from $9/user',
          savings: 'FROM $9',
          expires: 'Limited seats',
          type: 'deal',
        },
        {
          store: 'Alibaba',
          title: 'Accounting stack onboarding pack',
          savings: '$200 OFF',
          expires: 'New today',
          type: 'new',
        },
      ],
      playbook: [
        {
          title: 'Plan for Renewal, Not Just Year One',
          body: 'Discounts look great upfront; evaluate year-two pricing and migration costs before committing.',
        },
        {
          title: 'Bundle Around Core Workflow',
          body: 'Pick one anchor platform, then add tools with interoperable billing and data portability.',
        },
        {
          title: 'Use Trial Data to Negotiate',
          body: 'Volume, seat growth, and usage trends can unlock stronger negotiated terms than listed promos.',
        },
      ],
      faqs: [
        {
          question: 'Are business promos available for existing accounts?',
          answer:
            'Some are new-customer only, but annual upgrades may still qualify for retention incentives.',
        },
        {
          question: 'Can I combine onboarding credits with discount codes?',
          answer: 'Often yes, but this depends on vendor policy and billing cycle.',
        },
      ],
    },
  };

  private readonly slugAliases: Record<string, string> = {
    'mens-fashion': 'apparel',
    'womens-fashion': 'apparel',
    'kids-fashion': 'apparel',
    sportswear: 'apparel',
    'ai-writing': 'ai-tools',
    'image-generation': 'ai-tools',
    'video-tools': 'ai-tools',
    'ai-chatbots': 'ai-tools',
    smartphones: 'electronics',
    laptops: 'electronics',
    wearables: 'electronics',
    'audio-devices': 'electronics',
    'crm-platforms': 'business-software',
    'accounting-tools': 'business-software',
    'project-management': 'business-software',
    'team-collaboration': 'business-software',
  };

  readonly category: CategoryDetailData =
    this.categoryBySlug[this.slugAliases[this.categorySlug] ?? this.categorySlug] ??
    this.categoryBySlug['apparel'];

  // Deliberately NOT seeded from `this.category`: that lookup falls back to the sample
  // 'apparel' entry for any slug it doesn't know — which is nearly every real category — so it
  // printed "Premium Apparel Savings Hub" and apparel stores on top of unrelated categories.
  // Real values arrive in loadCategoryContent(); the template only renders these once
  // isCategoryLoading() clears, so an empty seed is never visible.
  readonly heroHeadline = signal('');
  readonly heroSummary = signal('');
  readonly categoryName = signal('');
  readonly isCategoryLoading = signal(true);
  /**
   * Live hero stats for this category. Only cards with a real number are kept: a category with no
   * products should not advertise "0 Total Products", and if nothing has been counted yet the hero
   * drops the whole row rather than showing four zeroes.
   */
  readonly statCards = signal<CategoryStatCard[]>([]);
  readonly topStores = signal<CategoryStore[]>([]);
  readonly faqs = signal<DetailFaq[]>([]);
  readonly bestCouponsDeals = signal<TrendingCouponItem[]>([]);
  readonly products = signal<CategoryProductCard[]>([]);
  // Blogs assigned to this category (admin-selected) for the "Insights & Perspectives" section.
  readonly categoryBlogs = signal<FrontendBlog[]>([]);
  readonly longContentHtml = signal('');
  readonly longformTitle = computed(() => {
    const categoryName = this.categoryName();
    return categoryName ? `Complete ${categoryName} Savings Guide` : 'Complete Savings Guide';
  });

  // Hero short description: clamp to 2 lines behind a "Read More" toggle when it's long enough.
  readonly isSummaryExpanded = signal(false);
  readonly shouldClampSummary = computed(() => this.heroSummary().trim().length > 140);
  toggleSummary(): void {
    this.isSummaryExpanded.update((value) => !value);
  }

  // Long-content guide: collapse tall CMS HTML behind a "Read More" toggle.
  readonly isLongContentExpanded = signal(false);
  readonly shouldCollapseLongContent = computed(() => this.longContentHtml().trim().length > 600);
  toggleLongContent(): void {
    this.isLongContentExpanded.update((value) => !value);
  }

  constructor() {
    // Fires immediately with the current params, then again on every category-to-category
    // navigation the router serves from this same component instance.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.categorySlug = params.get('categorySlug')?.toLowerCase().trim() ?? 'apparel';
      this.resetCategoryState();
      this.loadCategoryContent();
    });
  }

  /**
   * Clears everything the previous category put on the page.
   *
   * Without this, a category that returns none of a given section keeps showing the last one's —
   * a sub-category with no stores of its own rendered its parent's store list under its own name.
   */
  private resetCategoryState(): void {
    this.isCategoryLoading.set(true);
    this.categoryName.set('');
    this.heroHeadline.set('');
    this.heroSummary.set('');
    this.statCards.set([]);
    this.topStores.set([]);
    this.allStores.set([]);
    this.faqs.set([]);
    this.products.set([]);
    this.bestCouponsDeals.set([]);
    this.subCategories.set([]);
    this.categoryBlogs.set([]);
    this.longContentHtml.set('');
    this.isSummaryExpanded.set(false);
    this.isLongContentExpanded.set(false);
  }

  readonly competitorStores: CompetitorStore[] = [
    { name: 'Amazon', domain: 'amazon.com' },
    { name: 'Etsy', domain: 'etsy.com' },
    { name: "Kohl's", domain: 'kohls.com' },
    { name: 'Rakuten', domain: 'rakuten.com' },
    { name: 'Zaful', domain: 'zaful.com' },
    { name: 'Adulttime', domain: 'adulttime.com' },
    { name: 'Dresslily', domain: 'dresslily.com' },
    { name: 'Alibaba', domain: 'alibaba.com' },
    { name: 'Adidas', domain: 'adidas.com' },
    { name: 'Aerie', domain: 'aerie.com' },
    { name: 'Aeropostale', domain: 'aeropostale.com' },
    { name: 'Aldo', domain: 'aldo.com' },
    { name: 'AliExpress', domain: 'aliexpress.com' },
    { name: 'Armani', domain: 'armani.com' },
    { name: 'ASOS', domain: 'asos.com' },
    { name: 'Alo Yoga', domain: 'aloyoga.com' },
    { name: 'American Eagle', domain: 'ae.com' },
    { name: 'Anastasia Beverly Hills', domain: 'anastasiabeverlyhills.com' },
    { name: 'Ann Taylor', domain: 'anntaylor.com' },
    { name: 'Anthropologie', domain: 'anthropologie.com' },
    { name: 'Apple', domain: 'apple.com' },
    { name: 'Arcteryx', domain: 'arcteryx.com' },
    { name: 'Ardene', domain: 'ardene.com' },
    { name: 'Aritzia', domain: 'aritzia.com' },
    { name: 'Athleta', domain: 'athleta.com' },
    { name: 'Avenue', domain: 'avenue.com' },
    { name: 'Banana Republic', domain: 'bananarepublic.com' },
    { name: 'Best Buy', domain: 'bestbuy.com' },
    { name: "Bloomingdale's", domain: 'bloomingdales.com' },
    { name: 'Boohoo', domain: 'boohoo.com' },
    { name: 'Calvin Klein', domain: 'calvinklein.com' },
    { name: "Carter's", domain: 'carters.com' },
    { name: 'Coach', domain: 'coach.com' },
    { name: 'Costco', domain: 'costco.com' },
    { name: 'Crocs', domain: 'crocs.com' },
    { name: "Dick's Sporting Goods", domain: 'dickssportinggoods.com' },
    { name: 'DSW', domain: 'dsw.com' },
    { name: 'eBay', domain: 'ebay.com' },
    { name: 'Express', domain: 'express.com' },
    { name: 'Farfetch', domain: 'farfetch.com' },
    { name: 'Fashion Nova', domain: 'fashionnova.com' },
    { name: 'Foot Locker', domain: 'footlocker.com' },
    { name: 'Forever 21', domain: 'forever21.com' },
    { name: 'Gap', domain: 'gap.com' },
    { name: 'H&M', domain: 'hm.com' },
    { name: 'Hollister', domain: 'hollisterco.com' },
    { name: 'IKEA', domain: 'ikea.com' },
    { name: 'JCPenney', domain: 'jcpenney.com' },
    { name: "Levi's", domain: 'levi.com' },
    { name: "Macy's", domain: 'macys.com' },
    { name: 'Madewell', domain: 'madewell.com' },
    { name: 'Mango', domain: 'mango.com' },
    { name: 'Michael Kors', domain: 'michaelkors.com' },
    { name: 'Neiman Marcus', domain: 'neimanmarcus.com' },
    { name: 'New Balance', domain: 'newbalance.com' },
    { name: 'Nike', domain: 'nike.com' },
    { name: 'Nordstrom', domain: 'nordstrom.com' },
    { name: 'Old Navy', domain: 'oldnavy.com' },
    { name: 'Overstock', domain: 'overstock.com' },
    { name: 'Puma', domain: 'puma.com' },
    { name: 'Saks Fifth Avenue', domain: 'saksfifthavenue.com' },
    { name: 'Shein', domain: 'shein.com' },
    { name: 'Sephora', domain: 'sephora.com' },
    { name: 'Target', domain: 'target.com' },
    { name: 'Temu', domain: 'temu.com' },
    { name: 'The Home Depot', domain: 'homedepot.com' },
    { name: 'Ulta Beauty', domain: 'ulta.com' },
    { name: 'Under Armour', domain: 'underarmour.com' },
    { name: 'Walmart', domain: 'walmart.com' },
    { name: 'Wayfair', domain: 'wayfair.com' },
  ].map((store: CompetitorStoreSeed, index: number) => ({
    ...store,
    logoUrl: this.competitorLogoPool[index % this.competitorLogoPool.length],
    logoAlt: `${store.name} logo`,
  }));

  // Seeded empty on purpose. This used to start as the `competitorStores` sample list with
  // invented coupon counts, so every category that had no stores of its own — which is all of
  // them until a sync files stores under it — advertised Amazon, Etsy and Nike as its retailers.
  readonly allStores = signal<AllCategoryStore[]>([]);

  readonly visibleOffers = computed(() => {
    const tabId = this.selectedOfferTabId();
    if (tabId === 'all') {
      return this.category.offers;
    }
    return this.category.offers.filter((offer) => offer.type === tabId);
  });

  readonly selectedTrendingCouponTab = computed(
    () =>
      this.trendingCouponTabs.find((tab) => tab.id === this.selectedTrendingCouponTabId()) ??
      this.trendingCouponTabs[0],
  );

  readonly currentTrendingCoupons = computed(() => {
    const tabId = this.selectedTrendingCouponTabId();
    const coupons = this.bestCouponsDeals();

    if (tabId === 'all') {
      return coupons;
    }

    if (tabId === 'coupon') {
      return coupons.filter((coupon) => coupon.actionType === 'code');
    }

    if (tabId === 'deal') {
      return coupons.filter((coupon) => coupon.actionType === 'deal');
    }

    return coupons.filter((coupon) => coupon.isLatest);
  });

  readonly currentTrendingCouponsEmptyText = computed(() => {
    const tabId = this.selectedTrendingCouponTabId();

    if (tabId === 'coupon') {
      return 'No coupon codes are available for this category right now.';
    }

    if (tabId === 'deal') {
      return 'No deals are available for this category right now.';
    }

    if (tabId === 'new') {
      return 'No recently updated offers are available for this category right now.';
    }

    return 'No offers are available for this category right now.';
  });

  readonly currentTrendingCouponsHeading = computed(
    () => `Best ${this.categoryName() || 'Category'} Deals, Coupons & Offers`,
  );

  readonly selectedPopularCategoryTab = computed(
    () =>
      this.popularCategoryTabs().find((tab) => tab.id === this.selectedPopularCategoryTabId()) ??
      this.popularCategoryTabs()[0],
  );

  readonly popularCategoryTotalSlides = computed(() =>
    Math.max(
      1,
      this.selectedPopularCategoryTab().categories.length - this.popularCardsPerSlide + 1,
    ),
  );

  readonly popularCategorySlideIndexes = computed(() =>
    Array.from({ length: this.popularCategoryTotalSlides() }, (_, index) => index),
  );

  readonly visiblePopularCategories = computed(() => {
    const start = this.currentPopularCategorySlide();
    return this.selectedPopularCategoryTab().categories.slice(
      start,
      start + this.popularCardsPerSlide,
    );
  });

  readonly journalPosts: JournalPost[] = [
    {
      id: 1,
      category: 'Technology',
      title: 'The Future of AI in Modern Software Development',
      excerpt:
        'Explore how generative AI is reshaping the way developers write, test, and deploy code in 2024.',
      author: 'Alex Rivers',
      date: 'Oct 12, 2023',
      readTime: '5 min read',
      imageUrl:
        'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800',
      storeLogoUrl: '/images/top-stores/logo-adulttime.png',
    },
    {
      id: 2,
      category: 'Design',
      title: 'Minimalism: Why Less is More in UI/UX Design',
      excerpt:
        'Learn the principles of functional minimalism and how to create impactful interfaces with less clutter.',
      author: 'Sarah Chen',
      date: 'Oct 10, 2023',
      readTime: '4 min read',
      imageUrl:
        'https://images.unsplash.com/photo-1558655146-d09347e92766?auto=format&fit=crop&q=80&w=800',
      storeLogoUrl: '/images/top-stores/logo-amazon.png',
    },
    {
      id: 3,
      category: 'Strategy',
      title: 'Scaling Your Startup: From Seed to Series A',
      excerpt:
        'The critical milestones and cultural shifts required to successfully grow your business at speed.',
      author: 'Marcus Thorne',
      date: 'Oct 08, 2023',
      readTime: '8 min read',
      imageUrl:
        'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800',
      storeLogoUrl: '/images/top-stores/logo-rakuten.png',
    },
  ];

  readonly visibleJournalPosts = computed(() => {
    const tabId = this.selectedJournalTabId();

    if (tabId === 'all') {
      return this.journalPosts;
    }

    const selectedTab = this.journalTabs.find((tab) => tab.id === tabId);
    return this.journalPosts.filter((post) => post.category === selectedTab?.label);
  });

  selectOfferTab(tabId: OfferTab['id']): void {
    this.selectedOfferTabId.set(tabId);
  }

  selectTrendingCouponTab(tabId: OfferTab['id']): void {
    this.selectedTrendingCouponTabId.set(tabId);
  }

  selectPopularCategoryTab(tabId: string): void {
    this.selectedPopularCategoryTabId.set(tabId);
    this.currentPopularCategorySlide.set(0);
  }

  selectPopularCategorySlide(index: number): void {
    this.currentPopularCategorySlide.set(index);
  }

  selectJournalTab(tabId: string): void {
    this.selectedJournalTabId.set(tabId);
  }

  hasMetricCount(value: number | undefined): boolean {
    return typeof value === 'number' && Number.isFinite(value);
  }

  // "X Coupons & Y Deals" for admin-curated top stores; "N Coupons & Deals" for the real category
  // stores fallback (which only carry a single total count).
  storeCountLabel(store: CategoryStore): string {
    if (this.hasMetricCount(store.couponCodeCount) || this.hasMetricCount(store.couponDealCount)) {
      // Empty only when both counts are zero; fall through to the single-total label below
      // rather than render an empty line.
      const label = formatOfferCount(store.couponCodeCount ?? 0, store.couponDealCount ?? 0);
      if (label) {
        return label;
      }
    }
    const total = store.couponsCount ?? 0;
    return `${total} Coupon${total === 1 ? '' : 's'} & Deals`;
  }

  shouldShowMetricDivider(store: CategoryStore): boolean {
    return this.hasMetricCount(store.couponCodeCount) && this.hasMetricCount(store.couponDealCount);
  }

  metricLabel(value: number | undefined, singular: string, plural: string): string {
    return value === 1 ? singular : plural;
  }

  hasCouponsCount(value: number | undefined): boolean {
    return typeof value === 'number' && Number.isFinite(value);
  }

  private loadCategoryContent(): void {
    this.api
      .getCouponCategory(this.categorySlug)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.isCategoryLoading.set(false);
            this.applyCategoryNotFoundSeo();
            return;
          }

          const content = response.data.content;
          const headingH1 = this.readContentString(content, 'headingH1');
          const shortContent = this.readContentString(content, 'shortContent');
          const longContent = this.readString(content?.['longContent']);
          const categoryName = this.readString(response.data.name);
          const metaTitle = this.readContentString(content, 'metaTitle');
          const metaDescription = this.readContentString(content, 'metaDescription');
          const topStores = this.readTopStores(content);
          // One latest coupon per store in this category (from the API). Falls back to the
          // admin-curated "best coupons/deals" when the API doesn't return per-store coupons.
          const storeCoupons = response.data['storeCoupons'];
          const bestCouponsDeals = this.readBestCouponsDeals(
            Array.isArray(storeCoupons) && storeCoupons.length > 0
              ? storeCoupons
              : content?.['bestCouponsDeals'],
          );
          const allStores = this.readAllStores(response.data['stores']);
          const faqs = this.readFaqs(response.data.faqs);
          const products = this.readProducts(response.data['products']);
          const subCategories = this.readSubCategories(response.data['subCategories']);
          this.subCategories.set(subCategories);

          this.categoryName.set(categoryName);
          this.statCards.set(this.readStatCards(response.data['statCards']));
          this.faqs.set(faqs);
          this.products.set(products);
          const blogs = response.data['blogs'];
          this.categoryBlogs.set(Array.isArray(blogs) ? (blogs as FrontendBlog[]) : []);

          // Most categories have no CouponCategoryContent authored yet, so fall back to copy
          // derived from the category name — the same phrasing applyCategorySeo() already uses
          // for the meta title/description, so the visible hero and the metadata agree.
          this.heroHeadline.set(headingH1 || this.defaultHeadline(categoryName));
          this.heroSummary.set(shortContent || this.defaultSummary(categoryName));

          this.longContentHtml.set(longContent);

          // Assigned unconditionally: an empty result has to clear the section, not leave the
          // previous category's stores standing under this category's name.
          if (topStores.length) {
            this.topStores.set(topStores);
          } else {
            // No admin-curated top stores for this category — show its real stores instead, so
            // shoppers still see actual stores in "Top stores in <category>". Cap it as a
            // highlight row; the full list still renders in "All stores" below.
            this.topStores.set(
              allStores.slice(0, 8).map((store) => ({
                name: store.name,
                logoUrl: store.logoUrl,
                slug: store.slug,
                couponsCount: store.couponsCount,
              })),
            );
          }

          this.bestCouponsDeals.set(bestCouponsDeals);
          this.allStores.set(allStores);

          this.applyCategorySeo(metaTitle, metaDescription);

          this.isCategoryLoading.set(false);
        },
        error: (error: unknown) => {
          this.isCategoryLoading.set(false);

          // A malformed slug that maps onto a real category comes back as a 404 carrying where the
          // page actually lives, so the old address 301s instead of dying. Two categories were
          // stored with a trailing hyphen ("cars-"), and cleaning those up would otherwise break
          // every link to them. Same shape the comparison page uses for its reversed pairs.
          const redirectTo = (error as { error?: { data?: { redirectTo?: string } } })?.error?.data
            ?.redirectTo;
          if (redirectTo) {
            this.ssrResponse.redirectPermanent(redirectTo);
            this.router.navigateByUrl(redirectTo, { replaceUrl: true });
            return;
          }

          // Not every error means the category is missing. A 5xx or a timeout must not retire a
          // live category URL, so the cause is recorded before the not-found path runs.
          this.ssrResponse.noteFetchFailure(error);
          this.applyCategoryNotFoundSeo();
        },
      });
  }

  /**
   * SEO for a category page. The admin's Meta Title / Meta Description stay the source of truth
   * (they were already applied through Title/Meta before); SeoService adds the canonical, the
   * OG/Twitter tags and the structured data those manual calls never emitted.
   *
   * Called from inside the subscribe, so on the server the tags are set before the HTML is
   * serialised and reach crawlers in the initial response.
   */
  /**
   * Reads the API's `statCards` array defensively — the payload types these as `unknown`, and a
   * card with a zero count is dropped rather than displayed (see `statCards`).
   */
  private readStatCards(value: unknown): CategoryStatCard[] {
    if (!Array.isArray(value)) return [];

    const cards: CategoryStatCard[] = [];
    for (const entry of value) {
      const card = entry as Record<string, unknown>;
      const label = this.readString(card?.['label']).trim();
      const numeric = typeof card?.['value'] === 'number' ? (card['value'] as number) : 0;
      const displayValue = this.readString(card?.['displayValue']).trim();

      if (!label || !Number.isFinite(numeric) || numeric <= 0) continue;

      cards.push({
        key: this.readString(card?.['key']) || label,
        label,
        value: numeric,
        displayValue: displayValue || String(numeric),
      });
    }

    return cards;
  }

  /** Hero title when no admin `headingH1` exists for this category. */
  private defaultHeadline(name: string): string {
    return name ? `${name} Coupons, Promo Codes & Deals` : 'Coupons, Promo Codes & Deals';
  }

  /** Hero summary when no admin `shortContent` exists for this category. */
  private defaultSummary(name: string): string {
    return `Verified ${name || 'category'} coupon codes, promo codes and deals from the stores SavingArena tracks in this category.`;
  }

  private applyCategorySeo(metaTitle: string, metaDescription: string): void {
    const name = this.categoryName() || this.category.name;
    const path = `/categories/${this.categorySlug}`;
    const url = this.seo.absolute(path);

    const title = metaTitle || `${name} Coupons, Promo Codes & Deals | SavingArena`;
    const description =
      metaDescription ||
      `Verified ${name} coupon codes, promo codes and deals from the stores SavingArena tracks in this category.`;

    this.seo.apply({ title, description, canonicalPath: path });

    // FAQ answers are admin CMS HTML; schema.org wants text. The template renders this exact list
    // (it gates on `faqs().length`), so FAQPage is only emitted for FAQs the user can actually see.
    const faqs = this.faqs()
      .map((faq) => ({
        question: this.stripHtml(faq.question),
        answer: this.stripHtml(faq.answer),
      }))
      .filter((faq) => faq.question && faq.answer);

    this.seo.setJsonLd([
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
      faqs.length ? faqPageSchema(faqs) : null,
    ]);
  }

  /** An unknown category slug is a real 404, not a 200 rendering the static fallback content. */
  private applyCategoryNotFoundSeo(): void {
    this.ssrResponse.setNotFound();
    this.seo.apply({
      title: 'Category not found | SavingArena',
      description: 'The category you are looking for is not available on SavingArena.',
      noindex: true,
    });
    this.seo.setJsonLd([]);
  }

  private readContentString(content: Record<string, unknown> | undefined, key: string): string {
    const value = content?.[key];
    return this.stripHtml(this.readString(value));
  }

  private readTopStores(content: Record<string, unknown> | undefined): CategoryStore[] {
    const stores = content?.['topStores'];

    if (!Array.isArray(stores)) {
      return [];
    }

    return stores.filter(this.isRecord).map((store) => ({
      name: this.readString(store['name']),
      logoUrl: this.toAssetUrl(this.readString(store['logo'])),
      slug: this.readString(store['slug']),
      couponCodeCount: this.readNumber(store['couponCodeCount']),
      couponDealCount: this.readNumber(store['couponDealCount']),
    }));
  }

  private readBestCouponsDeals(coupons: unknown): TrendingCouponItem[] {
    if (!Array.isArray(coupons)) {
      return [];
    }

    const latestDateKey = this.latestCouponDateKey(coupons);

    return coupons.filter(this.isRecord).map((coupon, index) => {
      const store = this.isRecord(coupon['store']) ? coupon['store'] : undefined;
      const hasAffiliateCode = coupon['hasAffiliateCode'] === true;
      const couponCode = this.readString(coupon['couponCode']);
      const storeDomain = this.readString(store?.['domain']);
      const hasCode = hasAffiliateCode || Boolean(couponCode);
      const updatedDateKey = this.dateKey(this.readString(coupon['updatedAt']));

      return {
        id: this.readString(coupon['id']) || String(index),
        type: hasCode ? 'coupon' : 'deal',
        storeName: this.readString(store?.['name']),
        storeSlug: this.readString(store?.['slug']),
        storeDomain,
        logoUrl: this.toAssetUrl(this.readString(store?.['logoImagePath'])),
        title: this.readString(coupon['name']),
        expires: this.couponExpiryText(coupon),
        actionType: hasCode ? 'code' : 'deal',
        actionLabel: hasCode ? 'Reveal Code' : 'Get Deal',
        actionUrl: this.couponActionUrl(
          this.readString(coupon['id']),
          this.readString(store?.['slug']),
          storeDomain,
        ),
        isLatest: Boolean(latestDateKey && updatedDateKey === latestDateKey),
      };
    });
  }

  private couponExpiryText(coupon: Record<string, unknown>): string {
    const endDate = this.readString(coupon['endDate']);

    if (endDate) {
      const date = new Date(endDate);

      if (!Number.isNaN(date.getTime())) {
        const now = new Date();
        const daysLeft = Math.ceil((date.getTime() - now.getTime()) / 86_400_000);

        if (daysLeft < 0) {
          return 'Expired';
        }

        if (daysLeft === 0) {
          return 'Ends Today';
        }

        return `${daysLeft} ${daysLeft === 1 ? 'Day' : 'Days'} Left`;
      }
    }

    return this.readString(coupon['endDateText']);
  }

  /**
   * The `/go/` redirect, not the merchant URL.
   *
   * This used to read the store's `affiliateLink` straight out of the API payload and put it in the
   * href, which meant the raw affiliate URL was in the page for anyone to read and the click was
   * never recorded. The store page's own CTAs already went through `/go/`; this one did not.
   *
   * Falls back to the store's domain only when there is no coupon to redirect for, which is the same
   * "somewhere sensible rather than nowhere" behaviour it had before.
   */
  private couponActionUrl(couponId: string, storeSlug: string, storeDomain: string): string {
    const redirect = this.affiliate.couponUrl(storeSlug, couponId);
    if (redirect) return redirect;

    return storeDomain ? this.toExternalUrl(storeDomain) : '#';
  }

  private readFaqs(faqs: unknown): DetailFaq[] {
    if (!Array.isArray(faqs)) {
      return [];
    }

    return faqs
      .filter(this.isRecord)
      .map((faq) => ({
        question: this.readString(faq['question']),
        answer: this.readString(faq['answer']),
      }))
      .filter((faq) => faq.question || faq.answer);
  }

  private readSubCategories(subCategories: unknown): PopularCategoryItem[] {
    if (!Array.isArray(subCategories)) {
      return [];
    }

    return subCategories.filter(this.isRecord).map((category) => {
      const iconType = this.readString(category['iconType']);
      const iconImagePath = this.readString(category['iconImagePath']);
      const iconCode = this.readString(category['iconCode']);
      return {
        label: this.readString(category['name']),
        slug: this.readString(category['slug']),
        imageUrl: iconType === 'image' && iconImagePath ? this.toAssetUrl(iconImagePath) : '',
        iconHtml:
          iconType === 'code' && iconCode ? this.sanitizer.bypassSecurityTrustHtml(iconCode) : null,
      };
    });
  }

  private readAllStores(stores: unknown): AllCategoryStore[] {
    if (!Array.isArray(stores)) {
      return [];
    }

    return stores.filter(this.isRecord).map((store) => ({
      name: this.readString(store['name']),
      logoUrl: this.toAssetUrl(this.readString(store['logo'])),
      slug: this.readString(store['slug']),
      couponsCount: this.readNumber(store['couponsCount']),
    }));
  }

  private readProducts(products: unknown): CategoryProductCard[] {
    if (!Array.isArray(products)) {
      return [];
    }

    return products.filter(this.isRecord).map((product, index) => {
      const imagePath = this.readString(product['thumbnailImagePath']);

      return {
        id: this.readString(product['id']) || String(index),
        name: this.readString(product['name']),
        description: this.truncateText(this.readString(product['description']), 50),
        imageUrl: this.toAssetUrl(imagePath),
        hoverImageUrl: this.readHoverImageUrl(product['galleryImagePaths'], imagePath),
        isExternal: product['isExternal'] === true,
        // In-app product page for the card's "View Detail" action; '' when the feed has no slug.
        detailPath: this.readString(product['slug'])
          ? `/${this.readString(product['slug'])}`
          : '',
        oldPrice: this.readString(product['oldPrice']),
        newPrice: this.readString(product['newPrice']),
      };
    });
  }

  // The first gallery angle that genuinely differs from the thumbnail; '' when the product has none.
  private readHoverImageUrl(value: unknown, mainImagePath: string): string {
    if (!Array.isArray(value)) {
      return '';
    }

    const alternate = value
      .map((path) => this.readString(path))
      .find((path) => path && path !== mainImagePath);

    return alternate ? this.toAssetUrl(alternate) : '';
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private readString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private readNumber(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value !== 'string' || !value.trim()) {
      return undefined;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : undefined;
  }

  private stripHtml(value: string): string {
    return this.decodeEntities(value.replace(/<[^>]*>/g, ' '))
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Admin CMS text is HTML-encoded, so plain-text bindings show raw entities (e.g. "&amp;").
  // Decode the common ones (& last, so "&amp;lt;" doesn't collapse to "<").
  private decodeEntities(value: string): string {
    return value
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#0*39;|&apos;/g, "'")
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&');
  }

  private truncateText(value: string, limit: number): string {
    if (value.length <= limit) {
      return value;
    }

    return `${value.slice(0, limit).trimEnd()}...`;
  }

  private latestCouponDateKey(coupons: unknown[]): string {
    let latestTime = 0;
    let latestKey = '';

    for (const coupon of coupons) {
      if (!this.isRecord(coupon)) {
        continue;
      }

      const value = this.readString(coupon['updatedAt']);
      const date = new Date(value);

      if (Number.isNaN(date.getTime()) || date.getTime() <= latestTime) {
        continue;
      }

      latestTime = date.getTime();
      latestKey = this.dateKey(value);
    }

    return latestKey;
  }

  private dateKey(value: string): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString().slice(0, 10);
  }

  private toAssetUrl(path: string): string {
    if (!path || /^https?:\/\//i.test(path)) {
      return path;
    }

    const assetBaseUrl = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    const assetPath = path.startsWith('/') ? path : `/${path}`;

    return `${assetBaseUrl}${assetPath}`;
  }

  private toExternalUrl(value: string): string {
    if (!value || /^https?:\/\//i.test(value)) {
      return value;
    }

    return `https://${value}`;
  }
}
